/**
 * Autonoma AI — lightweight i18n
 *
 * A context + t() helper covering the app's visible strings (nav labels,
 * buttons, headings, common UI text) in en/es/pt/fr/de. English is the
 * fallback for any missing key.
 *
 * The app language is driven SOLELY by the merchant's Shopify admin
 * locale — no manual selector, no stored preference. Shopify passes the
 * locale as a `locale` query param on the embedded-app load (and via
 * App Bridge); we map it to our supported set by language prefix
 * ('es-ES' → 'es', 'pt-BR' → 'pt', ...) and fall back to 'en' for
 * anything else. The resolved language is synced to the backend
 * (store.settings.language) so ALL AI-generated content — ads, pages,
 * websites, advertorials, ad copy, video scripts, voiceover — is
 * produced in the same language.
 */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import en from './en';
import es from './es';
import pt from './pt';
import fr from './fr';
import de from './de';
import { apiFetch } from '../lib/api';

const DETECTED_KEY = 'autonoma_detected_locale';

const DICTIONARIES = { en, es, pt, fr, de };

/**
 * Resolve the Shopify admin locale to a supported language code.
 *
 * The `locale` query param is only reliably present on the initial
 * embedded load, so once seen it's remembered in localStorage for
 * in-app navigations and reloads. App Bridge exposes the same value
 * when available. Returns 'en' when nothing supported is found.
 */
function detectShopifyLocale() {
  try {
    const sources = [
      new URLSearchParams(window.location.search).get('locale'),
      // App Bridge (when the global is present) carries the admin locale.
      window.shopify?.config?.locale,
    ];
    for (const raw of sources) {
      if (!raw) continue;
      const code = String(raw).toLowerCase().split(/[-_]/)[0];
      if (DICTIONARIES[code]) {
        try { window.localStorage.setItem(DETECTED_KEY, code); } catch { /* private mode */ }
        return code;
      }
    }
    const remembered = window.localStorage.getItem(DETECTED_KEY);
    return DICTIONARIES[remembered] ? remembered : 'en';
  } catch {
    return 'en';
  }
}

function lookup(dict, key) {
  return key.split('.').reduce((node, part) => (node ? node[part] : undefined), dict);
}

function interpolate(str, vars) {
  if (!vars || typeof str !== 'string') return str;
  return str.replace(/\{(\w+)\}/g, (m, name) => (vars[name] === undefined || vars[name] === null ? m : String(vars[name])));
}

const LanguageContext = createContext({
  language: 'en',
  t: (key, vars) => interpolate(lookup(en, key) ?? key, vars),
});

export function LanguageProvider({ shop, children }) {
  const [language, setLanguageState] = useState(() => detectShopifyLocale());

  // Once the shop is known, re-resolve (the locale param may have been
  // consumed after first paint) and sync the Shopify-derived language
  // to the backend so AI generation follows it. Best-effort — the UI
  // never blocks on the save.
  useEffect(() => {
    if (!shop) return;
    const detected = detectShopifyLocale();
    setLanguageState(detected);
    // Only POST when the detected language differs from what this
    // browser last synced for this shop — every page load was writing
    // the same value.
    const syncKey = `autonoma_lang_synced:${shop}`;
    let last = null;
    try { last = window.localStorage.getItem(syncKey); } catch {}
    if (last === detected) return;
    apiFetch(`/api/settings/language`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ language: detected }),
    }).then((res) => {
      if (res && res.ok) { try { window.localStorage.setItem(syncKey, detected); } catch {} }
    }).catch(() => { /* generation falls back to the last synced value */ });
  }, [shop]);

  // t(key, vars?) — `vars` fills `{name}` placeholders in the string
  // (e.g. t('ui.progress.imagesOf', { done: 6, total: 15 })).
  const t = useCallback((key, vars) => {
    const dict = DICTIONARIES[language] || en;
    return interpolate(lookup(dict, key) ?? lookup(en, key) ?? key, vars);
  }, [language]);

  const value = useMemo(() => ({ language, t }), [language, t]);

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useTranslation() {
  return useContext(LanguageContext);
}

export default LanguageContext;
